import { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import { ReactComponent as TetherIcon } from '@/assets/tokens/tether-green.svg';
import { IoOpenOutline, IoCopyOutline, IoDocumentTextOutline } from 'react-icons/io5';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { Button, Input, FormGroup, Label, Alert } from 'reactstrap';
import TournamentDetailCard from './components/TournamentDetailCard';
import TournamentPlayersTable from './components/TournamentPlayersTable';
import TournamentTransactionsTable from './components/TournamentTransactionsTable';
import TournamentStatusIndicator from './components/TournamentStatusIndicator';
import DurationTimer from '@/components/DurationTimer';
import TournamentBlindLevelCard from './components/TournamentBlindLevelCard';
import StandardLayout from '@/common/layouts/StandardLayout';
import TournamentTablesDetails from './components/TournamentTablesDetails';

import useTournamentDetails from '@/hooks/useTournamentDetails';
import { TournamentInfoDto, TournamentStatus, TournamentType } from '@repo/common-types';
import { toast } from 'react-toastify';
import TournamentEvents from './components/TournamentEvents';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import TournamentQuickActions from '../components/TournamentQuickActions';

import BulkRegistrationSection from './components/BulkRegistrationSection';
import DevOnlySection from './components/DevOnlySection';
import PlayerMigrationSection from './components/PlayerMigrationSection';
import { config } from '@repo/common-types';
import ROUTES from '@/routes/routes-enums';
import useCancelTournament from '@/hooks/useCancelTournament';

const TournamentDetailsPage = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { tournament, refetchTournament, totalChips, updateTournament } = useTournamentDetails(tournamentId ?? '');
  const { cancelTournament } = useCancelTournament(tournamentId ?? '', tournament?.url ?? '');

  const [isPublicConfirm, setIsPublicConfirm] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState<any>(null);

  // Define runningTournamentStates at the top level
  const runningTournamentStates: TournamentStatus[] = [
    TournamentStatus.RUNNING,
    TournamentStatus.PAUSED,
    TournamentStatus.BREAK,
  ];

  // Move all useMemo hooks to the top before any conditional logic
  const confirmModalMessage = useMemo(() => {
    if (!confirmModalData || !tournament) {
      return '';
    }

    switch (confirmModalData.type) {
      case 'cancel':
        return `${runningTournamentStates.includes(tournament.status) ? 'This tournament is in progress! ' : ''}${'Are you sure you want to cancel '}${tournament.name}?`;
      case 'archive':
        return `${'Are you sure you want to archive '}${tournament.name}?`;
      default:
        return '';
    }
  }, [confirmModalData, tournament, runningTournamentStates]);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     refetchTournament();
  //   }, 4500);

  //   return () => clearInterval(interval);
  // }, [refetchTournament]);

  const handleConfirmIsPublicChange = async () => {
    try {
      if (!tournament) {
        return;
      }
      const nextValue = !tournament.isPublic;
      await updateTournament({ id: tournament.id, isPublic: nextValue });
      setIsPublicConfirm(false);
      toast.success(nextValue === true ? 'Tournament is now public' : 'Tournament is now private');
    } catch (error) {
      toast.error('Failed to update tournament');
    }
  };

  const handleConfirmCancelTournament = async () => {
    try {
      await cancelTournament(undefined, {
        onSuccess: () => {
          toast.success('Tournament queued for cancellation');
          setConfirmModalData(null);
        },
        onError: () => {
          return false;
        },
      });
    } catch (error) {
      toast.error('Failed to cancel tournament');
    }
  };

  const handleOpenLobby = () => {
    if (!tournament) {
      return;
    }
    window.open(`${process.env.REACT_APP_PHENOM_APP}/tournament/${tournament.id}`);
  };

  const handleOpenTournamentLogs = () => {
    if (!tournament?.id) return;
    const base = 'https://telemetry.betterstack.com/team/t238083/tail?s=969735,969739,969749';
    const query = encodeURIComponent(`metadata.tournamentId=${tournament.id}`);
    const url = `${base}&q=${query}&rf=now-7d`;
    window.open(url, '_blank');
  };

  const handleCopyContractAddress = async () => {
    if (!tournament?.contractAddress) return;
    try {
      await navigator.clipboard.writeText(tournament.contractAddress);
      toast.success('Contract address copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy address');
    }
  };

  const handleConfirmArchive = async () => {
    try {
      if (!tournament) {
        return;
      }
      await updateTournament({ id: tournament.id, deletedAt: new Date() });
      toast.success('Tournament archived');
      setConfirmModalData(null);
    } catch (error) {
      toast.error('Failed to archive tournament');
    }
  };

  const handleActionConfirmation = () => {
    switch (confirmModalData.type) {
      case 'cancel':
        handleConfirmCancelTournament();
        break;
      case 'archive':
        handleConfirmArchive();
        break;
    }
  };

  const handleBackClick = () => {
    // Navigate to appropriate list page based on tournament type
    if (tournament?.tournamentType === TournamentType.SNG) {
      navigate(ROUTES.SNG_LIST);
    } else {
      navigate(ROUTES.TOURNAMENTS_LIST);
    }
  };

  const handleCopyUrl = async () => {
    if (!tournament?.url) return;

    try {
      await navigator.clipboard.writeText(tournament.url);
      toast.success('Tournament URL copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  if (!tournament) {
    return null;
  }

  return (
    <StandardLayout
      pageTitle={tournament.name}
      showBackButton
      onBackClick={handleBackClick}
      headerButton={
        <div className="d-flex align-items-center justify-content-end flex-wrap gap-2">
          <FormGroup switch>
            <Input checked={tournament.isPublic} onClick={() => setIsPublicConfirm(true)} size={24} type="switch" />
            <Label check>Is Public</Label>
          </FormGroup>
          <TournamentQuickActions tournament={tournament} />
          <Button
            className="d-flex align-items-center justify-content-center"
            outline
            color="primary"
            onClick={handleOpenTournamentLogs}
            title="Logs"
          >
            <IoDocumentTextOutline size={20} />
          </Button>

          <Button
            onClick={handleOpenLobby}
            className="d-flex align-items-center justify-content-center gap-2"
            color="primary"
          >
            Lobby
            <IoOpenOutline className="mb-0 text-xl" size={20} />
          </Button>
        </div>
      }
    >
      {tournament.deletedAt && <Alert color="danger">This tournament is archived</Alert>}
      {isPublicConfirm && (
        <ConfirmModal
          message={`${tournament.isPublic ? 'Are you sure you want to make this tournament private?' : 'Are you sure you want to make this tournament public?'} This will affect this tournament's public visibility.`}
          onConfirm={handleConfirmIsPublicChange}
          onClose={() => setIsPublicConfirm(false)}
          title="Tournament Visibility"
          confirmButtonText={tournament.isPublic ? 'Yes, make private' : 'Yes, make public'}
          cancelButtonText={tournament.isPublic ? 'No, keep public' : 'No, keep private'}
        />
      )}
      {confirmModalData?.id && (
        <ConfirmModal
          onClose={() => setConfirmModalData(null)}
          onConfirm={handleActionConfirmation}
          title="Confirm"
          message={confirmModalMessage}
        />
      )}
      <div className="w-100 my-3">
        {/* Main Tournament Overview - Key Stats */}
        <div className="row g-4 mb-4">
          <div className="col-md-8">
            <div className="card h-100 rounded-3 border-0 shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex align-items-center mb-4">
                  <h5 className="card-title mb-0 me-3">Tournament Overview</h5>
                  <div className="ms-auto">
                    <TournamentStatusIndicator status={tournament.status} />
                  </div>
                </div>
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <div className="row">
                        <div className="col-6">
                          <small
                            className="text-muted text-uppercase fw-semibold"
                            style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                          >
                            Start Time
                          </small>
                          <div className="fw-semibold text-white" style={{ fontSize: '0.95rem' }}>
                            {tournament.startTime ? new Date(tournament.startTime).toLocaleString() : 'Not set'}
                          </div>
                        </div>
                        <div className="col-6">
                          <small
                            className="text-muted text-uppercase fw-semibold"
                            style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                          >
                            Duration
                          </small>
                          <div className="fw-semibold text-white" style={{ fontSize: '0.95rem' }}>
                            {tournament.status === TournamentStatus.RUNNING ||
                            tournament.status === TournamentStatus.COMPLETED ? (
                              <DurationTimer
                                completed={tournament.status === TournamentStatus.COMPLETED}
                                startTime={tournament.startTime ? new Date(tournament.startTime) : new Date()}
                                finishedAt={
                                  tournament.status === TournamentStatus.COMPLETED
                                    ? tournament.endTime
                                    : tournament.finishedAt
                                }
                              />
                            ) : (
                              '00:00:00'
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="row">
                        <div className="col-6">
                          <small
                            className="text-muted text-uppercase fw-semibold"
                            style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                          >
                            Table Size
                          </small>
                          <div className="fw-semibold text-white" style={{ fontSize: '0.95rem' }}>
                            {tournament.tableSize} seats
                          </div>
                        </div>
                        <div className="col-6">
                          <small
                            className="text-muted text-uppercase fw-semibold"
                            style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                          >
                            Players (Min/Max)
                          </small>
                          <div className="fw-semibold text-white" style={{ fontSize: '0.95rem' }}>
                            {tournament.minPlayers} / {tournament.maxPlayers}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <div className="row">
                        <div className="col-6">
                          <small
                            className="text-muted text-uppercase fw-semibold"
                            style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                          >
                            Players
                          </small>
                          <div className="fw-bold fs-5 text-white">
                            {tournament.playersRemaining} / {tournament.entries}
                          </div>
                        </div>
                        <div className="col-6">
                          <small
                            className="text-muted text-uppercase fw-semibold"
                            style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                          >
                            Total Chips
                          </small>
                          <div className="fw-bold fs-5 text-white">
                            {totalChips?.totalChips ? totalChips.totalChips.toLocaleString() : 'Loading...'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="row">
                        <div className="col-6">
                          <small
                            className="text-muted text-uppercase fw-semibold"
                            style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                          >
                            Reentries
                          </small>
                          <div className="fw-semibold text-white" style={{ fontSize: '0.95rem' }}>
                            {(tournament as any).totalReentries ?? 'N/A'}
                          </div>
                        </div>
                        <div className="col-6">
                          <small
                            className="text-muted text-uppercase fw-semibold"
                            style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                          >
                            Max Reentries
                          </small>
                          <div className="fw-semibold text-white" style={{ fontSize: '0.95rem' }}>
                            {tournament.maxReentries === 0 ? 'None' : (tournament.maxReentries ?? 'Unlimited')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100 rounded-3 border-0 shadow-sm">
              <div className="card-body p-4">
                <h5 className="card-title mb-4">Current Blind Level</h5>
                <TournamentBlindLevelCard
                  running={tournament.status === TournamentStatus.RUNNING}
                  levelEndAt={tournament.levelEndAt}
                  currentLevel={tournament.currentLevel}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div className="row g-4 mb-4">
          <div className="col-md-12">
            <div className="card rounded-3 border-0 shadow-sm">
              <div className="card-body p-4">
                <h5 className="card-title mb-4">Financial Information</h5>
                <div className="row g-4">
                  <div className="col-md-3 col-sm-6">
                    <div className="text-center">
                      <div className="d-flex align-items-center justify-content-center mb-2 gap-2">
                        <h2 className="fw-bold mb-0 text-white">{(tournament.prizePool ?? 0).toFixed(2)}</h2>
                        <TetherIcon style={{ width: '32px', height: '32px' }} />
                      </div>
                      <small
                        className="text-muted text-uppercase fw-semibold"
                        style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                      >
                        Prize Pool
                      </small>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div className="text-center">
                      <div className="d-flex align-items-center justify-content-center mb-2 gap-2">
                        <h2 className="fw-bold mb-0 text-white">{(tournament.fee ?? 0).toFixed(2)}</h2>
                        <TetherIcon style={{ width: '32px', height: '32px' }} />
                      </div>
                      <small
                        className="text-muted text-uppercase fw-semibold"
                        style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                      >
                        Rake
                      </small>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div className="text-center">
                      <div className="d-flex align-items-center justify-content-center mb-2 gap-2">
                        <h2 className="fw-bold mb-0 text-white">{(tournament.buyIn ?? 0).toFixed(2)}</h2>
                        <TetherIcon style={{ width: '32px', height: '32px' }} />
                      </div>
                      <small
                        className="text-muted text-uppercase fw-semibold"
                        style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                      >
                        Buy-in
                      </small>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div className="text-center">
                      <h2 className="fw-bold mb-0 text-white">{tournament.startingChips.toLocaleString()}</h2>
                      <small
                        className="text-muted text-uppercase fw-semibold d-block mt-2"
                        style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                      >
                        Starting Chips
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Information */}
        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <div className="card rounded-3 border-0 shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="card-title mb-0">Tournament URL</h5>
                  <Button
                    size="sm"
                    color="primary"
                    onClick={handleCopyUrl}
                    className="d-flex align-items-center gap-2 px-3"
                  >
                    <IoCopyOutline size={16} />
                    Copy
                  </Button>
                </div>
                <div className="bg-light rounded border p-3">
                  <code
                    className="text-wrap"
                    style={{ fontSize: '0.875rem', color: '#6c757d', wordBreak: 'break-all' }}
                  >
                    {tournament.url}
                  </code>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card rounded-3 border-0 shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="card-title mb-0">Contract Address</h5>
                  <Link
                    target="_blank"
                    to={`${config.deposits.polygon.blockExplorerUrl}/address/${tournament.contractAddress}`}
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 px-3"
                  >
                    <FaExternalLinkAlt size={14} />
                    Explorer
                  </Link>
                </div>
                <div className="bg-light d-flex align-items-center justify-content-between gap-3 rounded border p-3">
                  <code
                    className="text-wrap"
                    style={{ fontSize: '0.875rem', color: '#6c757d', wordBreak: 'break-all' }}
                  >
                    {tournament.contractAddress}
                  </code>
                  <Button
                    size="sm"
                    color="secondary"
                    outline
                    onClick={handleCopyContractAddress}
                    className="d-flex align-items-center gap-2 px-3"
                  >
                    <IoCopyOutline size={16} />
                    Copy
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Tables Section */}
      <div className="w-100 my-3">
        <TournamentTablesDetails />
      </div>

      {/* Players Section */}
      <div className="w-100 my-3">
        <TournamentPlayersTable />
      </div>

      {/* Payouts Section */}
      <div className="w-100 my-3">
        <TournamentTransactionsTable />
      </div>

      {/* Events Section */}
      <div className="w-100 my-3">
        <TournamentEvents />
      </div>

      {/* Bulk Registration Section - Only shown in non-production environments */}
      <BulkRegistrationSection tournament={tournament} onRefetch={refetchTournament} />

      {/* Player Migration Section */}
      <PlayerMigrationSection tournament={tournament} onRefetch={refetchTournament} />

      {/* Tournament Controls Section */}
      <DevOnlySection tournament={tournament} onRefetch={refetchTournament} />
    </StandardLayout>
  );
};

export default TournamentDetailsPage;
